import { useEffect, useRef, useState } from 'react';

import { apiV1Base } from '../lib/apiConfig';
export type Bar = { t: string; o: number; h: number; l: number; c: number; v: number };
export type Stats = {
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
  change_pct: number | null;
};
export type Quote = {
  symbol: string;
  asset_class: 'us_equity' | 'crypto';
  timeframe: '1D' | '1W' | '1M' | '1Y';
  bars: Bar[];
  stats: Stats;
  message?: string;
};

const POLL_MS = 30_000;

function apiBase(): string {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  return apiV1Base();
}

export function useSymbolQuote(
  symbol: string,
  timeframe: '1D' | '1W' | '1M' | '1Y',
  assetClass: 'us_equity' | 'crypto' = 'us_equity',
) {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const symRef = useRef(symbol);
  symRef.current = symbol;

  useEffect(() => {
    if (!symbol) {
      setQuote(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();

    const fetchOnce = async () => {
      try {
        const url = `${apiBase()}/quote/${encodeURIComponent(symbol)}/bars?timeframe=${timeframe}&asset_class=${assetClass}`;
        const res = await fetch(url, { signal: controller.signal, cache: 'no-store' });
        if (!res.ok) {
          if (!cancelled) {
            setError(`Quote unavailable (HTTP ${res.status}).`);
            setLoading(false);
          }
          return;
        }
        const body = await res.json();
        if (cancelled) return;
        if (!body?.ok) {
          setError(body?.error?.message || 'Quote unavailable.');
          setLoading(false);
          return;
        }
        setQuote(body.data as Quote);
        setError(null);
        setLoading(false);
      } catch (err: any) {
        if (cancelled || err?.name === 'AbortError') return;
        setError(err?.message || 'Network error fetching quote.');
        setLoading(false);
      }
    };

    setLoading(true);
    fetchOnce();
    const interval = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        fetchOnce();
      }
    }, POLL_MS);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(interval);
    };
  }, [symbol, timeframe, assetClass]);

  return { quote, loading, error };
}

export default useSymbolQuote;
